import type {AdapterAPI} from '../../api/AdapterAPI';
import {Analytics} from '../../core/Analytics';
import {Event} from '../../enums/Event';
import type {AnalyticsConfig} from '../../types/AnalyticsConfig';
import type {AnalyticsStateMachineOptions} from '../../types/AnalyticsStateMachineOptions';
import type {CustomDataValues} from '../../types/CustomDataValues';
import {logger} from '../../utils/Logger';
import {
  hasPlayerAlreadyBeenAugmented,
  isDefined,
  markPlayerInstanceAsAugmented,
} from '../../utils/playerAugmentationUtils';
import {VERSION} from '../../Version';
import type {InternalAdapterAPI} from '../internal/InternalAdapterAPI';

import {ShakaInternalAdapter} from './ShakaInternalAdapter';

export class ShakaAdapter implements AdapterAPI {
  private readonly internalAdapter: InternalAdapterAPI | undefined;
  private readonly analytics: Analytics | undefined;

  constructor(config: AnalyticsConfig, player: any, opts?: AnalyticsStateMachineOptions) {
    if (hasPlayerAlreadyBeenAugmented(player)) {
      logger.errorMessageToUser('Bitmovin Analytics is already hooked up to this player instance');
      return;
    }
    markPlayerInstanceAsAugmented(player);

    this.internalAdapter = new ShakaInternalAdapter(player, opts);
    this.analytics = Analytics.create(config, this.internalAdapter);
    this.wrapPlayerLoad(player);
  }

  // The Shaka player's `load()` method does not trigger error events for exceptions thrown while loading the manifest.
  // Therefore, we wrap this method and catch exceptions and forward them to analytics.
  // TODO [AN-4149]: this needs to be reevaluated, since the cypress test with a none existing manifest is not working
  private wrapPlayerLoad(player: any) {
    // Check if `player.load` can be overwritten
    const playerPrototype = Object.getPrototypeOf(player); // in the case of the player proxy, the load method is in the prototype
    const loadPropertyDescriptor =
      Object.getOwnPropertyDescriptor(player, 'load') ?? Object.getOwnPropertyDescriptor(playerPrototype, 'load');
    if (loadPropertyDescriptor?.writable !== true && loadPropertyDescriptor?.set === undefined) {
      // The player instance might be a proxy (e.g., from Shaka UI Library), making `player.load` a getter-only accessor.
      // This prevents us to override `load` function with our error interception logic.
      logger.warn(
        'Bitmovin Analytics is unable to intercept player.load method. The provided Shaka player instance appears to be a ' +
          'proxy object (e.g. created via Shaka UI Library). Ensure you provide the local player instance instead.',
      );
      return;
    }

    const originalLoad = player.load;
    /**
     * Avoid passing `this` (the adapter reference) to an interceptor or its error handler.
     *
     * Reason:
     * Passing `this` can lead to memory leaks. When that shaka player is destroyed, it does not clean up the `player.load`
     * method, which we intercept. This leaves the old adapter and interceptor still referenced, even though the shaka player
     * has been destroyed. If a new adapter is then created for the same shaka player, a new interceptor will also be created.
     * This new interceptor will wrap the `player.load` method again, which was already wrapped by the previous adapter,
     * leading to unnecessary memory usage and potential memory leaks.
     */
    const thatInternalAdapter = this.internalAdapter as ShakaInternalAdapter;

    player.load = (...args) =>
      new Promise((resolve, reject) => {
        originalLoad
          .apply(player, args)
          .then((result) => resolve(result))
          .catch((error) => {
            const eventObject = {
              currentTime: 0,
              // See https://github.com/shaka-project/shaka-player/blob/main/lib/util/error.js
              code: error?.code ?? undefined,
              message: JSON.stringify({category: error?.category, severity: error?.severity}),
              data: {additionalData: JSON.stringify(error?.data)},
            };

            thatInternalAdapter.eventCallback(Event.ERROR, eventObject);

            // eslint-disable-next-line @typescript-eslint/prefer-promise-reject-errors
            reject(error);
          });
      });
  }

  static readonly version: string = VERSION;

  get version(): string {
    return VERSION;
  }

  getCurrentImpressionId(): string | undefined {
    if (!isDefined(this.analytics)) return;

    return this.analytics.getCurrentImpressionId();
  }

  getUserId(): string | undefined {
    if (!isDefined(this.analytics)) return;

    return this.analytics.getUserId();
  }

  setCustomData(values: CustomDataValues) {
    if (!isDefined(this.internalAdapter)) return;

    this.internalAdapter.setCustomData(values);
  }

  setCustomDataOnce(values: CustomDataValues) {
    if (!isDefined(this.analytics)) return;

    this.analytics.setCustomDataOnce(values);
  }

  sourceChange(config: AnalyticsConfig) {
    if (!isDefined(this.analytics)) return;

    this.analytics.sourceChange(config);
  }
}
